import { checklistStrings, placeholderStrings } from '@data/membershipForm';
import { Checklist } from '@models/Checklist';
import { Company, PatchCompany } from '@models/Company';
import { country } from '@models/Country';
import { AdminUser, PatchCompanyPrincipal } from '@models/User';
import {
  createField,
  createGoodsField,
  createYesNoField,
  faxField,
  formStrings,
  requiredPositiveIntegerField,
  newEmailField,
  newPasswordField,
  phoneField,
  requiredStringField,
  stringField,
  positiveIntegerField,
} from '@utils/forms';
import { createStrings } from '@utils/local';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    agreeToPrivacyPolicy: 'プライバシーポリシーをご確認ください。',
    englishNameError: 'アルファベットで入力してください',
    corporateNumberError: '13桁の半角数字で入力してください。',
    yearError: '西暦を４桁の数字でご入力ください',
    yes: 'はい',
    no: 'いいえ',
    have: 'あります',
    notHave: 'ありません',
    hope: '希望します',
    hopeNot: '希望しません',
    got: '取得しています',
    haveNotGot: '取得していません',
    japan: '日本',
    china: '中国',
    city: '都市',
    existed: 'FC既存顧客',
  },
});

export class MembershipFormFields {
  static instance: MembershipFormFields | undefined;

  static get shared() {
    if (MembershipFormFields.instance === undefined) {
      MembershipFormFields.instance = new MembershipFormFields();
    }
    return MembershipFormFields.instance;
  }

  companyName = createField(
    (label) => requiredStringField(label, 1, 128),
    'companyName',
    {
      ja: '会社名',
    },
    {
      placeholder: placeholderStrings.companyName,
    },
  );

  phone = createField(
    (label) => phoneField(label),
    'phone',
    {
      ja: '電話番号',
    },
    {
      placeholder: placeholderStrings.phone,
    },
  );

  password = createField((label) => newPasswordField(label), 'password', {
    ja: 'パスワード',
  });

  confirmPassword = createField((label) => newPasswordField(label), 'confirmPassword', {
    ja: 'パスワード再入力',
  });

  agreeToPrivacyPolicy = createField(
    () => h.array(t.string).required(strings.agreeToPrivacyPolicy),
    'agreeToPrivacyPolicy',
    {
      ja: 'プライバシーポリシー',
    },
  );

  englishName = createField(
    () => h.string().matches(/^[ -~]+$/, strings.englishNameError),
    'englishName',
    {
      ja: '英字社名',
    },
    {
      placeholder: placeholderStrings.englishName,
    },
  );

  corporateNumber = createField(
    () => h.string().matches(/^[0-9]{13}$/, strings.corporateNumberError),
    'corporateNumber',
    {
      ja: '法人番号',
    },
    {
      placeholder: placeholderStrings.corporateNumber,
      tips: placeholderStrings.corporateNumberTips,
    },
  );

  country = createField(
    (label) => requiredStringField(label),
    'country',
    {
      ja: '国または地域',
    },
    {
      placeholder: '',
      choose: true,
      options: [
        { value: 'jp', label: strings.japan },
        { value: 'cn', label: strings.china },
      ],
    },
  );

  zipcode = createField(
    (label) => h.string().matches(/^[0-9]+-?[0-9]*$/, formStrings.invalid(label)),
    'zipcode',
    {
      ja: '郵便番号',
    },
    {
      placeholder: placeholderStrings.zipcode,
    },
  );

  zipcodeLoose = createField(
    (label) =>
      t.union([t.literal(''), h.string().matches(/^[0-9]+-?[0-9]*$/, formStrings.invalid(label))]),
    'zipcode',
    {
      ja: '郵便番号',
    },
    {
      omittable: true,
      placeholder: placeholderStrings.zipcode,
    },
  );

  address1 = createField(
    (label) => requiredStringField(label, 1),
    'address1',
    {
      ja: '住所1',
    },
    {
      placeholder: placeholderStrings.address1,
    },
  );

  address2 = createField(
    (label) => requiredStringField(label),
    'address2',
    {
      ja: '住所2',
    },
    {
      placeholder: placeholderStrings.address2,
    },
  );

  address2Loose = createField(
    (label) => stringField(label, 0, 128),
    'address2',
    {
      ja: '住所2',
    },
    {
      placeholder: placeholderStrings.address2,
    },
  );

  city = createField(
    (label) => requiredStringField(label, 1),
    'address1',
    {
      ja: '都市',
    },
    {
      placeholder: placeholderStrings.city,
    },
  );

  address = createField(
    (label) => requiredStringField(label, 1),
    'address2',
    {
      ja: '住所',
    },
    {
      placeholder: placeholderStrings.address2,
    },
  );

  fax = createField(
    (label) => t.union([t.literal(''), faxField(label)]),
    'fax',
    {
      ja: 'FAX番号',
    },
    {
      omittable: true,
      placeholder: placeholderStrings.fax,
    },
  );

  startDate = createField(
    (label) => requiredStringField(label).matches(/^[0-9]{4}$/, strings.yearError),
    'startDate',
    {
      ja: '設立年',
    },
    {
      placeholder: placeholderStrings.startDate,
    },
  );

  startDateLoose = createField(
    (label) => stringField(label).matches(/^([0-9]{4}|[0-9]{0})$/, strings.yearError),
    'startDate',
    {
      ja: '設立年',
    },
    {
      placeholder: placeholderStrings.startDate,
    },
  );

  representerFirstName = createField(
    (label) => requiredStringField(label),
    'representerFirstName',
    {
      ja: '代表者名',
    },
  );

  representerFirstNameLoose = createField((label) => stringField(label), 'representerFirstName', {
    ja: '代表者名',
  });

  representerLastName = createField((label) => requiredStringField(label), 'representerLastName', {
    ja: '代表者姓',
  });

  representerLastNameLoose = createField((label) => stringField(label), 'representerLastName', {
    ja: '代表者姓',
  });

  legalCapital = createField(
    (label) => requiredPositiveIntegerField(label),
    'legalCapital',
    {
      ja: '資本金',
    },
    {
      placeholder: placeholderStrings.legalCapital,
    },
  );

  legalCapitalLoose = createField(
    (label) => positiveIntegerField(label),
    'legalCapital',
    {
      ja: '資本金',
    },
    {
      placeholder: placeholderStrings.legalCapital,
    },
  );

  industry = createField(
    (label) => requiredStringField(label),
    'industry',
    {
      ja: '業種',
    },
    {
      placeholder: placeholderStrings.industry,
    },
  );

  industryLoose = createField(
    (label) => stringField(label),
    'industry',
    {
      ja: '業種',
    },
    {
      placeholder: placeholderStrings.industry,
    },
  );

  employeeCount = createField(
    (label) => requiredPositiveIntegerField(label),
    'employeeCount',
    {
      ja: '社員数',
    },
    {
      placeholder: placeholderStrings.employeeCount,
    },
  );

  employeeCountLoose = createField(
    (label) => positiveIntegerField(label),
    'employeeCount',
    {
      ja: '社員数',
    },
    {
      placeholder: placeholderStrings.employeeCount,
    },
  );

  transactionCondition = createField(
    (label) => requiredStringField(label, 1, 1024),
    'transactionCondition',
    {
      ja: 'お取引条件',
    },
  );

  transactionConditionDetail = createField(
    (label) => stringField(label, 0, 1024),
    'transactionConditionDetail',
    {
      ja: 'お取引条件詳細',
    },
    {
      multiline: true,
    },
  );

  principalName = createField((label) => requiredStringField(label), 'principalName', {
    ja: '担当者名',
  });

  principalNameLoose = createField((label) => stringField(label), 'principalName', {
    ja: '担当者名',
  });

  principalFirstName = createField((label) => requiredStringField(label), 'principalFirstName', {
    ja: '担当者名',
  });

  principalFirstNameLoose = createField((label) => stringField(label), 'principalFirstName', {
    ja: '担当者名',
  });

  principalLastName = createField((label) => requiredStringField(label), 'principalLastName', {
    ja: '担当者姓',
  });

  firstName = createField((label) => requiredStringField(label), 'firstName', {
    ja: '名',
  });

  lastName = createField((label) => requiredStringField(label), 'lastName', {
    ja: '姓',
  });

  principalCellphone = createField(
    (label) => phoneField(label),
    'principalCellphone',
    {
      ja: '携帯電話番号',
    },
    {
      placeholder: placeholderStrings.phone,
    },
  );

  principalCellphoneLoose = createField(
    (label) => t.union([t.literal(''), phoneField(label)]),
    'principalCellphone',
    {
      ja: '携帯電話番号',
    },
    {
      placeholder: placeholderStrings.phone,
    },
  );

  principalLandline = createField(
    (label) => t.union([t.literal(''), phoneField(label)]),
    'principalLandline',
    {
      ja: '固定電話番号',
    },
    {
      omittable: true,
      placeholder: placeholderStrings.phone,
    },
  );

  principalEmail = createField(
    (label) => newEmailField(label),
    'principalEmail',
    {
      ja: 'メールアドレス',
    },
    {
      placeholder: placeholderStrings.email,
    },
  );

  snsId = createField(
    (label) => stringField(label),
    'snsId',
    {
      ja: 'SNS ID',
    },
    {
      omittable: true,
      placeholder: placeholderStrings.snsId,
    },
  );

  question1 = createYesNoField(
    'question1',
    checklistStrings.question1,
    strings.yes,
    strings.no,
    undefined,
  );

  question2 = createYesNoField(
    'question2',
    checklistStrings.question2,
    strings.have,
    strings.notHave,
    'Y',
    checklistStrings.question2Placeholder,
    checklistStrings.question2Prompt,
  );

  question3 = createYesNoField(
    'question3',
    checklistStrings.question3,
    strings.have,
    strings.notHave,
    'Y',
    checklistStrings.question3Placeholder,
    checklistStrings.question3Prompt,
  );

  question4 = createYesNoField(
    'question4',
    checklistStrings.question4,
    strings.hope,
    strings.hopeNot,
    undefined,
  );

  question5 = createYesNoField(
    'question5',
    checklistStrings.question5,
    strings.have,
    strings.notHave,
    'Y',
    checklistStrings.question5Placeholder,
    undefined,
    {
      multiline: true,
    },
  );

  question6 = createYesNoField(
    'question6',
    checklistStrings.question6,
    strings.got,
    strings.haveNotGot,
    'Y',
    checklistStrings.question6Placeholder,
    checklistStrings.question6Prompt,
  );

  question7 = createYesNoField(
    'question7',
    checklistStrings.question7,
    strings.have,
    strings.notHave,
    'Y',
    checklistStrings.question7Placeholder,
    undefined,
    {
      multiline: true,
    },
  );

  question8 = createGoodsField(
    'question8',
    checklistStrings.question8,
    checklistStrings.question8LegalQuestion,
  );

  question9 = createGoodsField(
    'question9',
    checklistStrings.question9,
    checklistStrings.question9LegalQuestion,
  );

  question10 = createYesNoField(
    'question10',
    checklistStrings.question10,
    strings.yes,
    strings.no,
    undefined,
  );

  question11 = createYesNoField(
    'question11',
    checklistStrings.question11,
    strings.yes,
    strings.no,
    undefined,
  );

  toCreateAccountSchema() {
    return t.type({
      companyName: this.companyName.type,
      lastName: this.lastName.type,
      firstName: this.firstName.type,
      principalCellphone: this.principalCellphone.type,
      principalEmail: this.principalEmail.type,
      password: this.password.type,
      confirmPassword: this.confirmPassword.type,
      agreeToPrivacyPolicy: this.agreeToPrivacyPolicy.type,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  toCreateAccountInitialValue(): t.TypeOf<
    ReturnType<MembershipFormFields['toCreateAccountSchema']>
  > {
    return {
      companyName: '',
      lastName: '',
      firstName: '',
      principalCellphone: '',
      principalEmail: '',
      password: '',
      confirmPassword: '',
      agreeToPrivacyPolicy: [],
    };
  }

  toCompanyJPFormSchema() {
    return t.type({
      companyName: this.companyName.type,
      englishName: this.englishName.type,
      corporateNumber: this.corporateNumber.type,
      country: country,
      zipcode: this.zipcode.type,
      address1: this.address1.type,
      address2: this.address2.type,
      phone: this.phone.type,
      fax: this.fax.type,
      startDate: this.startDate.type,
      representerLastName: this.representerLastName.type,
      representerFirstName: this.representerFirstName.type,
      legalCapital: this.legalCapital.type,
      industry: this.industry.type,
      employeeCount: this.employeeCount.type,
      transactionCondition: this.transactionCondition.type,
      transactionConditionDetail: this.transactionConditionDetail.type,
    });
  }

  toCompanyJPFormSchemaLoose() {
    return t.type({
      companyName: this.companyName.type,
      englishName: this.englishName.type,
      corporateNumber: this.corporateNumber.type,
      country: country,
      zipcode: this.zipcodeLoose.type,
      address1: this.address1.type,
      address2: this.address2Loose.type,
      phone: this.phone.type,
      fax: this.fax.type,
      startDate: this.startDateLoose.type,
      representerLastName: this.representerLastNameLoose.type,
      representerFirstName: this.representerFirstNameLoose.type,
      legalCapital: this.legalCapitalLoose.type,
      industry: this.industryLoose.type,
      employeeCount: this.employeeCountLoose.type,
      transactionCondition: this.transactionCondition.type,
      transactionConditionDetail: this.transactionConditionDetail.type,
    });
  }

  toCompanyCNFormSchema() {
    return t.type({
      companyName: this.companyName.type,
      englishName: this.englishName.type,
      corporateNumber: this.corporateNumber.type,
      country: country,
      zipcode: this.zipcode.type,
      address1: this.city.type,
      address2: this.address.type,
      phone: this.phone.type,
      fax: this.fax.type,
      startDate: this.startDate.type,
      representerLastName: this.representerLastName.type,
      representerFirstName: this.representerFirstName.type,
      legalCapital: this.legalCapital.type,
      industry: this.industry.type,
      employeeCount: this.employeeCount.type,
      transactionCondition: this.transactionCondition.type,
      transactionConditionDetail: this.transactionConditionDetail.type,
    });
  }

  toCompanyFormInitialValue(): t.TypeOf<ReturnType<MembershipFormFields['toCompanyJPFormSchema']>> {
    return {
      companyName: '',
      englishName: '',
      corporateNumber: '',
      country: 'jp',
      zipcode: '',
      address1: '',
      address2: '',
      phone: '',
      fax: '',
      startDate: '',
      representerLastName: '',
      representerFirstName: '',
      legalCapital: '' as any,
      industry: '',
      employeeCount: '' as any,
      transactionCondition: '',
      transactionConditionDetail: '',
    };
  }

  toPrincipalFormSchema() {
    return t.type({
      principalLastName: this.principalLastName.type,
      principalFirstName: this.principalFirstName.type,
      principalCellphone: this.principalCellphone.type,
      principalLandline: this.principalLandline.type,
      principalEmail: this.principalEmail.type,
      snsId: this.snsId.type,
    });
  }

  toPrincipalFormSchemaLoose() {
    return t.type({
      principalLastName: this.principalLastName.type,
      principalFirstName: this.principalFirstNameLoose.type,
      principalCellphone: this.principalCellphoneLoose.type,
      principalLandline: this.principalLandline.type,
      principalEmail: this.principalEmail.type,
      snsId: this.snsId.type,
    });
  }

  toPrincipalFormInitialValue(): t.TypeOf<
    ReturnType<MembershipFormFields['toPrincipalFormSchema']>
  > {
    return {
      principalLastName: '',
      principalFirstName: '',
      principalCellphone: '',
      principalLandline: '',
      principalEmail: '',
      snsId: '',
    };
  }

  toMembershipFormSchema(isJapan?: boolean) {
    return t.type({
      ...(isJapan ? this.toCompanyJPFormSchema().props : this.toCompanyCNFormSchema().props),
      ...this.toPrincipalFormSchema().props,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  toMembershipFormInitialValue(): t.TypeOf<
    ReturnType<MembershipFormFields['toMembershipFormSchema']>
  > {
    return {
      ...this.toCompanyFormInitialValue(),
      ...this.toMembershipFormInitialValue(),
    };
  }

  toChecklistFormSchema() {
    return t.type({
      question1: this.question1.type,
      question2: this.question2.type,
      question3: this.question3.type,
      question4: this.question4.type,
      question5: this.question5.type,
      question6: this.question6.type,
      question7: this.question7.type,
      question8: this.question8.type,
      question9: this.question9.type,
      question10: this.question10.type,
      question11: this.question11.type,
    });
  }

  toChecklistShortFormSchema() {
    return t.type({
      question2: this.question2.type,
      question3: this.question3.type,
      question6: this.question6.type,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  toChecklistFormInitialValue(): t.TypeOf<
    ReturnType<MembershipFormFields['toChecklistFormSchema']>
  > {
    return {
      question1: {
        answer: '',
        reason: '',
      },
      question2: {
        answer: '',
        reason: '',
      },
      question3: {
        answer: '',
        reason: '',
      },
      question4: {
        answer: '',
        reason: '',
      },
      question5: {
        answer: '',
        reason: '',
      },
      question6: {
        answer: '',
        reason: '',
      },
      question7: {
        answer: '',
        reason: '',
      },
      question8: {
        territory: '',
        types: '',
        amount: '' as any,
        illegal: '',
      },
      question9: {
        territory: '',
        types: '',
        amount: '' as any,
        illegal: '',
      },
      question10: {
        answer: '',
        reason: '',
      },
      question11: {
        answer: '',
        reason: '',
      },
    };
  }

  mapCompanyFormToCompany(m: CompanyForm): PatchCompany {
    return {
      company_name: m.companyName,
      english_name: m.englishName,
      corporate_number: m.corporateNumber,
      country: m.country,
      zip_code: m.zipcode,
      address1: m.address1,
      address2: m.address2,
      phone: m.phone,
      fax: m.fax,
      start_date: `${m.startDate}-01-01`, // We only need to input year, but the backend needs date like YYYY-MM-DD
      representer_first_name: m.representerFirstName,
      representer_last_name: m.representerLastName,
      legal_capital: m.legalCapital,
      industry: m.industry,
      employee_count: m.employeeCount,
      transaction_condition: m.transactionCondition === 'Y',
      transaction_condition_detail: m.transactionConditionDetail,
      tos_agreed: true,
      customer_grade: undefined,
      customer_next_grade: undefined,
      customer_next_grade_update_date: undefined,
    };
  }

  mapCompanyFormLooseToCompany(m: CompanyFormLoose): PatchCompany {
    return {
      company_name: m.companyName,
      english_name: m.englishName,
      corporate_number: m.corporateNumber,
      country: m.country,
      zip_code: m.zipcode,
      address1: m.address1,
      address2: m.address2,
      phone: m.phone,
      fax: m.fax,
      start_date: m.startDate ? `${m.startDate}-01-01` : null, // We only need to input year, but the backend needs date like YYYY-MM-DD
      representer_first_name: m.representerFirstName,
      representer_last_name: m.representerLastName,
      legal_capital: m.legalCapital,
      industry: m.industry,
      employee_count: m.employeeCount,
      transaction_condition: m.transactionCondition === 'Y',
      transaction_condition_detail: m.transactionConditionDetail,
      tos_agreed: true,
      customer_grade: undefined,
      customer_next_grade: undefined,
      customer_next_grade_update_date: undefined,
    };
  }

  mapPrincipalFormToPatchPrincipal(m: PrincipalForm): PatchCompanyPrincipal {
    return {
      first_name: m.principalFirstName,
      last_name: m.principalLastName,
      cellphone: m.principalCellphone,
      email: m.principalEmail,
      landline: m.principalLandline,
      sns_id: m.snsId,
    };
  }

  mapCompanyUserToMembershipForm(c: Company, u: AdminUser): MembershipForm {
    return {
      ...this.mapCompanyToCompanyForm(c),
      ...this.mapCompanyPrincipalToPrincipalForm(u),
    };
  }

  mapCompanyToCompanyForm(c: Company): CompanyForm {
    return {
      companyName: c.company_name,
      englishName: c.english_name,
      corporateNumber: c.corporate_number,
      country: c.country ?? '日本',
      zipcode: c.zip_code,
      address1: c.address1,
      address2: c.address2,
      phone: c.phone,
      fax: c.fax,
      startDate: c.start_date ? c.start_date.split('-')[0] : '', // Simply parse YYYY-MM-DD
      representerFirstName: c.representer_first_name,
      representerLastName: c.representer_last_name,
      legalCapital: c.legal_capital ?? ('' as any),
      industry: c.industry,
      employeeCount: c.employee_count ?? ('' as any),
      transactionCondition: c.transaction_condition ? 'Y' : 'N',
      transactionConditionDetail: c.transaction_condition_detail,
    };
  }

  mapCompanyPrincipalToPrincipalForm(p: AdminUser): PrincipalForm {
    return {
      principalFirstName: p.first_name,
      principalLastName: p.last_name,
      principalCellphone: p.cellphone,
      principalEmail: p.email,
      principalLandline: p.landline,
      snsId: '',
    };
  }

  mapChecklistToChecklistForm(c: Checklist): ChecklistForm {
    const i = this.toChecklistFormInitialValue();

    return {
      question1: {
        ...i.question1,
        ...c.question1,
      },
      question2: {
        ...i.question2,
        ...c.question2,
      },
      question3: {
        ...i.question3,
        ...c.question3,
      },
      question4: {
        ...i.question4,
        ...c.question4,
      },
      question5: {
        ...i.question5,
        ...c.question5,
      },
      question6: {
        ...i.question6,
        ...c.question6,
      },
      question7: {
        ...i.question7,
        ...c.question7,
      },
      question8: {
        ...i.question8,
        ...c.question8,
      },
      question9: {
        ...i.question9,
        ...c.question9,
      },
      question10: {
        ...i.question10,
        ...c.question10,
      },
      question11: {
        ...i.question11,
        ...c.question11,
      },
    };
  }

  toSharedInfoSchema() {
    return t.type({
      companyName: this.companyName.type,
      zipcode: this.zipcode.type,
      address1: this.address1.type,
      address2: this.address2.type,
      principalFirstName: this.principalFirstName.type,
      principalLastName: this.principalLastName.type,
      principalCellphone: this.principalCellphone.type,
      principalEmail: this.principalEmail.type,
      snsId: this.snsId.type,
    });
  }

  mapCompanyUserToSharedInfoInitialValues(c: Company, u: AdminUser): SharedInfoForm {
    return {
      companyName: c.company_name,
      zipcode: c.zip_code,
      address1: c.address1,
      address2: c.address2,
      principalFirstName: u.first_name,
      principalLastName: u.last_name,
      principalCellphone: u.cellphone,
      principalEmail: u.email,
      snsId: u.sns_id,
    };
  }
}

export type MembershipForm = t.TypeOf<ReturnType<MembershipFormFields['toMembershipFormSchema']>>;

export type CompanyForm = t.TypeOf<ReturnType<MembershipFormFields['toCompanyJPFormSchema']>>;

export type CompanyFormLoose = t.TypeOf<
  ReturnType<MembershipFormFields['toCompanyJPFormSchemaLoose']>
>;

export type PrincipalForm = t.TypeOf<ReturnType<MembershipFormFields['toPrincipalFormSchema']>>;

export type ChecklistForm = t.TypeOf<ReturnType<MembershipFormFields['toChecklistFormSchema']>>;

export type SharedInfoForm = t.TypeOf<ReturnType<MembershipFormFields['toSharedInfoSchema']>>;
